const { setCookie, parseCookies } = require("nookies");
const { queryGenderCookieValue } = require("./queryGender");

const cookiesAge = 60 * 60; // 1jam
const referralCookieAge = 30 * 24 * 60 * 60; // 30 hari
const referralCodePattern = /^[a-z0-9]{1,16}$/;

const hasFbclid = (referer) => {
    if (referer) {
        // Referer is attacker/bot-controlled and often not a valid absolute
        // URL (scanner probes routinely send garbage like "-", a bare path,
        // or no scheme at all). `new URL()` throws TypeError: Invalid URL on
        // any of those, and since this runs unconditionally on every request
        // via setInitialCookie() in server.js's catch-all handler, an
        // uncaught throw here crashed SSR for such requests and fired a
        // spurious [web-ssr-render] ops alert (22 Sep 26, e.g. a scanner
        // probe to /wp-admin/css/ with a non-URL Referer).
        try {
            return new URL(referer).searchParams.get("fbclid");
        } catch (e) {
            return null;
        }
    }
    return null;
};

const getIsHasFbclidCookie = (ctx) => {
    let fbCookieCLID = null;
    if (ctx) {
        fbCookieCLID = parseCookies(ctx).fbclid;
    } else {
        fbCookieCLID = parseCookies({}).fbclid;
    }

    if (fbCookieCLID) {
        return fbCookieCLID;
    }
    return null;
};

const PAYMENT_COUNTRY_QUERY_KEYS = ["country", "payment_country", "country_code"];

const normalizePaymentCountry = (country) => {
    if (!country) {
        return null;
    }

    const value = `${country}`.trim();
    if (!value) {
        return null;
    }

    const code = value.toLowerCase();
    if (code === "undefined" || code === "null") {
        return null;
    }
    if (code === "worldwide" || code === "global") {
        return "worldwide";
    }

    const upperCode = value.toUpperCase();
    return /^[A-Z]{2}$/.test(upperCode) ? upperCode : null;
};

const getCountryFromQueryObject = (query = {}) => {
    for (let i = 0; i < PAYMENT_COUNTRY_QUERY_KEYS.length; i += 1) {
        const value = query[PAYMENT_COUNTRY_QUERY_KEYS[i]];
        const normalized = normalizePaymentCountry(Array.isArray(value) ? value[0] : value);
        if (normalized) {
            return normalized;
        }
    }
    return null;
};

const getCountryFromUrl = (url) => {
    if (!url || url.indexOf("?") === -1) {
        return null;
    }

    const params = new URLSearchParams(url.split("?")[1]);
    for (let i = 0; i < PAYMENT_COUNTRY_QUERY_KEYS.length; i += 1) {
        const normalized = normalizePaymentCountry(params.get(PAYMENT_COUNTRY_QUERY_KEYS[i]));
        if (normalized) {
            return normalized;
        }
    }
    return null;
};

const getExplicitPaymentCountry = (ctx) => {
    const countryFromQuery = ctx && ctx.query ? getCountryFromQueryObject(ctx.query) : null;
    if (countryFromQuery) {
        return countryFromQuery;
    }

    const countryFromRequestUrl = ctx && ctx.req ? getCountryFromUrl(ctx.req.url) : null;
    if (countryFromRequestUrl) {
        return countryFromRequestUrl;
    }

    if (typeof window !== "undefined" && window.location) {
        return getCountryFromUrl(window.location.search);
    }

    return null;
};

const getPaymentCountryCookie = (ctx) => {
    const cookies = parseCookies(ctx || {});
    return (
        normalizePaymentCountry(cookies.payment_country)
        // Fallback to cf_country cookie populated by edge layer (Cloudflare /
        // Next.js middleware). Prevents FORCED-US fallback when only the cf
        // cookie is set on the browser (Trello WEB card 8 May 26 item 11).
        || normalizePaymentCountry(cookies.cf_country)
    );
};

const getHeaderCountry = (ctx) => {
    const headers = ctx && ctx.req && ctx.req.headers ? ctx.req.headers : {};
    return normalizePaymentCountry(
        headers["x-client-country-code"]
        || headers["x-http-ipcountry"]
        || headers["cf-ipcountry"]
        || headers["x-vercel-ip-country"]
    );
};

/**
 * Resolve payment/pricing country for Cleeng landing + checkout.
 *
 * Priority (owner LIVE GEO WINS 2026-07-10):
 * 1. Explicit query/url (?country= / payment_country / country_code) — switcher URL + local QA
 * 2. LIVE CF: cf-ipcountry header (SSR edge) OR always-refreshed cf_country cookie
 * 3. payment_country cookie — last-resort fallback only (stale geo must not beat live CF)
 *
 * Local E2E force: set BOTH payment_country + cf_country, and/or use ?country=.
 * Manual ChooseCountryModal may be overridden by live CF on next SSR (owner-accepted).
 */
const getPaymentCountry = (ctx) => {
    const explicit = getExplicitPaymentCountry(ctx);
    if (explicit) {
        return explicit;
    }

    // LIVE GEO WINS: edge header first, then always-refreshed cf_country cookie.
    const headers = ctx && ctx.req && ctx.req.headers ? ctx.req.headers : {};
    const liveCf = normalizePaymentCountry(headers["cf-ipcountry"])
        || normalizePaymentCountry(parseCookies(ctx || {}).cf_country);
    if (liveCf) {
        return liveCf;
    }

    // Other client/proxy country headers (non-CF) before stale payment cookie.
    const headerCountry = getHeaderCountry(ctx);
    if (headerCountry) {
        return headerCountry;
    }

    const cookies = parseCookies(ctx || {});
    return normalizePaymentCountry(cookies.payment_country);
};

const setInitialCookie = (req, res) => {
    const isHasFbclid = hasFbclid(req.headers.referer);
    if (isHasFbclid) {
        setCookie({ res }, "fbclid", isHasFbclid, {
            maxAge: cookiesAge,
            path: "/",
        });
    }
    if (req.query.l) {
        setCookie({ res }, "squestionnaire", req.query.l, {
            maxAge: cookiesAge,
            path: "/",
        });
    }
    if (req.query.r) {
        setCookie({ res }, "reference", req.query.r, {
            maxAge: cookiesAge,
            path: "/",
        });
    }
    // Trello SALES TRACKER (626): keep a valid referral code for 30 days
    // (last valid click wins, anything else is ignored) so it survives the
    // quiz, register and checkout; sent as tracking.referral_code.
    if (typeof req.query.r === "string" && referralCodePattern.test(req.query.r)) {
        setCookie({ res }, "mfs_r", req.query.r, {
            maxAge: referralCookieAge,
            path: "/",
            sameSite: "lax",
            secure: process.env.NODE_ENV === "production",
        });
    }
    // ?g= used to store the r value here; keep only a valid male/female.
    const queryGender = queryGenderCookieValue(req.query.g);
    if (queryGender) {
        setCookie({ res }, "gender", queryGender, {
            maxAge: cookiesAge,
            path: "/",
        });
    }
};

const setCookiePayment = (countryId) => {
    const paymentCountry = normalizePaymentCountry(countryId);
    if (paymentCountry) {
        setCookie(null, "payment_country", paymentCountry, {
            maxAge: 60 * 60 * 24,
            path: "/",
        });
    }
};
const seetPaymentCountry = (req, res) => {
    const explicitCountry = getExplicitPaymentCountry({ req });
    if (explicitCountry) {
        setCookie({ res }, "payment_country", explicitCountry, {
            maxAge: 60 * 60 * 24,
            path: "/",
        });
        return;
    }

    // Refresh payment_country from live CF when absent or diverged (LIVE GEO WINS).
    // Compare payment_country only — do not use getPaymentCountryCookie (cf fallback).
    if (req.headers["cf-ipcountry"]) {
        const cfIpCountry = normalizePaymentCountry(req.headers["cf-ipcountry"]);
        const paymentCountryOnly = normalizePaymentCountry(
            parseCookies({ req }).payment_country
        );
        if (cfIpCountry && cfIpCountry !== paymentCountryOnly) {
            setCookie({ res }, "payment_country", cfIpCountry, {
                maxAge: 60 * 60 * 24,
                path: "/",
            });
        }
    }
};

const setGeolocationCookie = (req, res) => {
    if (!req.path.startsWith("/api/")) {
        const explicitCountry = getExplicitPaymentCountry({ req });
        if (explicitCountry) {
            setCookie({ res }, "payment_country", explicitCountry, {
                maxAge: cookiesAge,
                path: "/",
            });
        }
        // setCookie({ res }, "cf_country", "US", {
        //     maxAge: cookiesAge,
        //     path: "/",
        // });
        // setCookie({ res }, "cf_connecting_ip", "203.0.113.45", {
        //     maxAge: cookiesAge,
        //     path: "/",
        // });
        // Refresh payment_country from live CF when absent or diverged (LIVE GEO WINS).
        // Keep explicit ?country= / setCookiePayment writes for THIS request.
        // Compare payment_country only — getPaymentCountryCookie also matches cf_country.
        if (req.headers["cf-ipcountry"]) {
            const cfIpCountry = normalizePaymentCountry(req.headers["cf-ipcountry"]);
            const paymentCountryOnly = normalizePaymentCountry(
                parseCookies({ req }).payment_country
            );
            if (!explicitCountry && cfIpCountry && cfIpCountry !== paymentCountryOnly) {
                setCookie({ res }, "payment_country", cfIpCountry, {
                    maxAge: cookiesAge,
                    path: "/",
                });
            }
        }
        if (req.headers["cf-ipcountry"]) {
            setCookie({ res }, "cf_country", req.headers["cf-ipcountry"], {
                maxAge: cookiesAge,
                path: "/",
            });
        }
        if (req.headers["cf-connecting-ip"]) {
            setCookie({ res }, "cf_connecting_ip", req.headers["cf-connecting-ip"], {
                maxAge: cookiesAge,
                path: "/",
            });
        }
    }
};

/**
 * Resolve Cloudflare country for X-HTTP-IPCOUNTRY / geo gate.
 *
 * Priority (LIVE GEO WINS 2026-07-10 — mirrors getPaymentCountry SSR policy):
 * 1. When ctx.req present: live cf-ipcountry header FIRST (VPN switch must not lag one reload)
 * 2. cf_country cookie fallback (client-only calls with no req, or missing edge header)
 *
 * Cookie-first was the VPN lag root cause: stale cf_country (IN) beat live GB header,
 * _app geo gate 302-skipped Plan GIP, and new-geo pricing only loaded on the NEXT request.
 */
const getCFCountry = (ctx) => {
    if (ctx && ctx.req && ctx.req.headers) {
        const liveCf = normalizePaymentCountry(ctx.req.headers["cf-ipcountry"]);
        if (liveCf) {
            return liveCf;
        }
    }

    const cookies = parseCookies(ctx || {});
    return normalizePaymentCountry(cookies.cf_country);
};

/**
 * Resolve Cloudflare connecting IP for X-HTTP-CONNECTING-IP.
 * SSR: live cf-connecting-ip header first; cookie fallback for client-only.
 */
const getCFIP = (ctx) => {
    if (ctx && ctx.req && ctx.req.headers && ctx.req.headers["cf-connecting-ip"]) {
        return ctx.req.headers["cf-connecting-ip"];
    }

    const cookies = parseCookies(ctx || {});
    return cookies.cf_connecting_ip || null;
};
module.exports = {
    setInitialCookie,
    setGeolocationCookie,
    getCFCountry,
    getCFIP,
    getPaymentCountry,
    getExplicitPaymentCountry,
    getPaymentCountryCookie,
    seetPaymentCountry,
    setCookiePayment,
    getIsHasFbclidCookie
};
